import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { ShieldCheck, CheckCircle2, Clock } from "lucide-react";
import SignaturePad from "@/components/app/SignaturePad";
import { apiError } from "@/lib/apiError";

// Direct axios (no auth) since this is a public route
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicConsent() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [sig, setSig] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/consents/${token}`);
      setDoc(data);
    } catch { setNotFound(true); }
    finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!sig) return toast.error("Please sign inside the box.");
    if (!typedName.trim() || typedName.trim().length < 2) return toast.error("Please type your full name.");
    if (!agreed) return toast.error("Please confirm you have read and understood the consent.");
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/consents/${token}/sign`, { signature: sig, typed_name: typedName.trim() });
      toast.success("Thank you — your consent has been recorded.");
      load();
    } catch (err) { toast.error(apiError(err, "Failed to sign")); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-ink-mid text-sm">Loading…</div>;
  if (notFound) return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="text-center max-w-md">
        <div className="w-14 h-14 rounded-2xl bg-[#FFE9E9] text-[#8B1E1E] grid place-items-center mx-auto mb-4"><ShieldCheck className="w-6 h-6" /></div>
        <div className="font-display text-2xl text-ink">Link invalid or expired</div>
        <div className="text-sm text-ink-mid mt-2">Please contact the clinic that sent you this link.</div>
      </div>
    </div>
  );
  const signed = !!doc?.patient_signed_at;

  return (
    <div className="min-h-screen bg-linen/30 py-8 px-4">
      <Toaster />
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-pine text-linen rounded-3xl p-6 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-linen/20 grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
            <div>
              <div className="overline opacity-70">Consent form</div>
              <div className="font-display text-xl">{doc.clinic_name}</div>
            </div>
          </div>
          <div className="mt-4 font-display text-2xl">{doc.title}</div>
          <div className="text-sm opacity-80 mt-1">For {doc.patient_name}{doc.tooth ? ` · Tooth ${doc.tooth}` : ""}{doc.estimated_cost ? ` · ₹${Number(doc.estimated_cost).toLocaleString("en-IN")}` : ""}</div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-3xl p-6 space-y-5 border border-[#E8EAE9]">
          <div>
            <div className="overline text-ink-soft">What will be done</div>
            <p className="text-sm text-ink/80 mt-1">{doc.procedure_description}</p>
            {doc.procedure_details && <p className="text-sm text-ink-mid mt-2 italic">{doc.procedure_details}</p>}
          </div>
          <div>
            <div className="overline text-ink-soft">Benefits</div>
            <ul className="list-disc pl-5 text-sm text-ink/80 mt-1 space-y-1">
              {doc.benefits.map((b, i) => <li key={`b-${i}-${b.slice(0,24)}`}>{b}</li>)}
            </ul>
          </div>
          <div className="rounded-2xl border-2 border-[#E5B34B] bg-[#FFF8E7] p-4">
            <div className="overline text-[#B0521F]">Risks &amp; complications</div>
            <ul className="list-disc pl-5 text-sm text-ink/80 mt-1 space-y-1">
              {doc.risks.map((r, i) => <li key={`r-${i}-${r.slice(0,24)}`}>{r}</li>)}
            </ul>
          </div>
          <div>
            <div className="overline text-ink-soft">Alternative treatments</div>
            <ul className="list-disc pl-5 text-sm text-ink/80 mt-1 space-y-1">
              {doc.alternatives.map((a, i) => <li key={`a-${i}-${a.slice(0,24)}`}>{a}</li>)}
            </ul>
          </div>
          {doc.extra_notes && (
            <div className="rounded-2xl bg-linen/50 p-3 border border-[#E8EAE9]">
              <div className="overline text-ink-soft">Additional note from your dentist</div>
              <div className="text-sm text-ink/80 mt-1">{doc.extra_notes}</div>
            </div>
          )}
          <div className="text-[11px] text-ink-soft border-t border-[#E8EAE9] pt-4">
            Dentist: <strong className="text-ink">{doc.dentist_name}</strong> · Clinic: {doc.clinic_name}
          </div>
        </div>

        {/* Sign panel */}
        {signed ? (
          <div className="mt-4 bg-white rounded-3xl p-8 text-center border-2 border-[#2D6A4F]" data-testid="already-signed-panel">
            <div className="w-14 h-14 rounded-2xl bg-[#E8F0EB] text-[#2D6A4F] grid place-items-center mx-auto mb-3"><CheckCircle2 className="w-7 h-7" /></div>
            <div className="font-display text-2xl text-ink">Consent recorded</div>
            <div className="text-sm text-ink-mid mt-2">Signed by <strong>{doc.patient_typed_name}</strong> on {new Date(doc.patient_signed_at).toLocaleString()}</div>
            <div className="text-xs text-ink-soft mt-4">You may close this window. A copy has been kept by the clinic for its records.</div>
          </div>
        ) : (
          <div className="mt-4 bg-white rounded-3xl p-6 border border-[#E8EAE9] space-y-4">
            <div className="font-display text-xl text-ink">Your acknowledgement</div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="consent-agree" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1 w-4 h-4 accent-pine" />
              <span className="text-sm text-ink/80">
                I have read and understood the procedure, benefits, risks, and alternatives. I have had the opportunity to ask questions. I voluntarily consent to this treatment.
              </span>
            </label>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Type your full name</label>
              <input data-testid="consent-typed-name" placeholder="Full legal name" value={typedName}
                onChange={(e) => setTypedName(e.target.value)}
                className="w-full rounded-xl border border-[#E8EAE9] px-4 py-2.5 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-ink-soft mb-1 block">Sign below (finger or mouse)</label>
              <SignaturePad testid="patient-sig" onChange={setSig} height={180} />
            </div>
            <button data-testid="consent-submit-btn" onClick={submit} disabled={submitting}
              className="w-full btn-primary text-sm disabled:opacity-60">
              {submitting ? "Recording…" : "Sign & submit consent"}
            </button>
            <div className="text-[10px] text-ink-soft text-center">Your IP address and browser info are recorded as part of the audit trail for legal validity.</div>
          </div>
        )}
      </div>
    </div>
  );
}
